import { type BadgeVariants, TextBadge } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

import { ToolTipOverlay } from './tool-tip-overlay';

const useStyles = createUseStyles({
    badges: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
});

const BADGE_TYPOGRAPHY_PROPS = { numberOfLines: 1 };

export type TextBadgeWithCountProps = {
    hasTooltip?: boolean;
    items: string | string[];
    tooltipClassName?: string;
    tooltipId: string;
    variant?: BadgeVariants;
};

/**
 * Renders a badge for the first item and, when more follow, a `+n` badge standing in for the rest. Both sit inside
 * one tooltip listing every item, so hovering either badge reads the whole list and the row never has to grow to
 * fit it.
 *
 * @param props - component props
 * @returns the badge row, or null when there is nothing to badge
 */
export function TextBadgeWithCount({
    hasTooltip = true,
    items,
    tooltipClassName,
    tooltipId,
    variant,
}: TextBadgeWithCountProps) {
    const classes = useStyles();

    const labels = Array.isArray(items) ? items : [items];

    if (!labels.length) {
        return null;
    }

    const hiddenCount = labels.length - 1;
    const hasHiddenItems = hiddenCount > 0;
    const shouldShowTooltip = hasTooltip && hasHiddenItems;
    const tooltipText = shouldShowTooltip ? labels.join(', ') : undefined;

    return (
        <ToolTipOverlay className={tooltipClassName} text={tooltipText} tooltipId={tooltipId}>
            <div className={classes.badges}>
                <TextBadge typographyProps={BADGE_TYPOGRAPHY_PROPS} variant={variant}>
                    {labels[0]}
                </TextBadge>

                {hasHiddenItems && (
                    <TextBadge typographyProps={BADGE_TYPOGRAPHY_PROPS} variant={variant}>
                        {`+${hiddenCount}`}
                    </TextBadge>
                )}
            </div>
        </ToolTipOverlay>
    );
}
